import { Editor } from "@tinymce/tinymce-react";
import { safeApiCall } from "api/common.service";
import { getImage } from "api/image.service";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import {
  EditorContentCss,
  EditorSkinUrl,
  getPluginList,
  getToolbarButtons,
  RTFDefaultStyles,
} from "./RTFConstants";

// ==== Types ====
type Props = {
  content?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  variant?: "minimal" | "full";
  height?: number;
  minHeight?: number;
};

export type RichTextEditorRef = {
  getHTML: () => string;
  reset: () => void;
};

// ==== Component ====
const RichTextEditor = forwardRef<RichTextEditorRef, Props>(
  (
    {
      content = "",
      onChange,
      placeholder = "Start writing...",
      readOnly = false,
      variant = "full",
      height = 400,
      minHeight = 580,
    },
    ref,
  ) => {
    const editorRef = useRef<any>(null);
    const [initialContent] = useState(content);

    useImperativeHandle(ref, () => ({
      getHTML: () => editorRef.current?.getContent() || "",
      reset: () => editorRef.current?.setContent(initialContent || ""),
    }));

    return (
      <Editor
        tinymceScriptSrc="/vendor/tinymce/tinymce.min.js"
        onInit={(_, editor) => {
          editorRef.current = editor;
        }}
        initialValue={initialContent}
        onEditorChange={(html: string) => {
          onChange?.(html);
        }}
        init={{
          height: height || 400,
          min_height: minHeight || 580,
          theme: "silver",
          menubar: variant === "full",
          plugins: getPluginList(),
          toolbar: getToolbarButtons(),
          placeholder,
          promotion: false,
          paste_data_images: true,
          invalid_elements: "script",
          images_file_types: "jpg,jpeg,png",
          file_picker_types: "image",
          extended_valid_elements: "svg[*]",
          skin_url: EditorSkinUrl,
          content_css: EditorContentCss,
          image_list: async function (success: any) {
            const images = await safeApiCall(getImage());
            success(images ?? []);
          },
          content_style: RTFDefaultStyles,
        }}
      />
    );
  },
);

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;
